import { DealConfidenceLevelId, DealResponse, DealStatus, DealStatusId } from '@business/entities/deal';
import { CommonService, getFieldValueString, IntegrationService } from '@business/services';
import { Formatter } from '@core/utils/formatter';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { ContactEmailText } from '@modules/common/ContactEmailText';
import { HasBilling } from '@modules/common/HasBilling';
import { PhoneActions } from '@modules/common/PhoneActions';
import { openSelectContactModal } from '@modules/common/SelectContactModal';
import { openSelectDateModal } from '@modules/common/SelectDateModal';
import { TextBlock } from '@modules/common/TextBlock';
import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { Card, CardBody } from 'reactstrap';
import { useObservable } from '@core/utils/hooks/rxjs';
import { FormSelect } from '@core/components/form';
import { formControl } from '@core/utils/form';
import { openDealLostModal } from './DealLostModal';
import { DealOverlayBloc } from './DealOverlayBloc';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { useCompany } from '@core/utils/hooks/misc';
import { IntegrationType } from '@business/entities/integration';
import { openSelectVendastaAccountModal } from '@modules/common/SelectVendastaAccountModal';
import { openOrganizationOverlay } from '@modules/organizations/OrganizationOverlay';
import { EstimatorAvailabilityBadge, getEstimatorLabel } from '@modules/common/EstimatorAvailabilityBadge';
import { openGenericSelectModal } from '@modules/common/GenericSelectModal';
import { resolve } from '@core/utils/ioc';
import { Blocs } from '@business/blocs';

export interface DealDetailsProps {
  deal: DealResponse;
  bloc: DealOverlayBloc;
}

export const DealDetails = memo((props: DealDetailsProps) => {
  const { deal, bloc } = props;

  const settings = useSettings();
  const company = useCompany();
  const dealStatuses = useObservable(bloc.dealStatuses$, []);
  const disqualificationRules = useObservable(bloc.disqualificationRules$, []);
  const confidenceLevels = useObservable(bloc.confidenceLevels$, []);
  const estimators = useObservable(bloc.estimators$, []);

  const selectedEstimator = useMemo(() => estimators.find(e => e.id === deal.estimatorUserId) ?? null, [estimators, deal.estimatorUserId]);

  const statusControl = useMemo(
    () => formControl<DealStatusId>({ placeholder: 'Select Status', initialValue: deal.dealStatus?.id }),
    [deal.dealStatus?.id],
  );
  const confidenceLevelControl = useMemo(
    () => formControl<DealConfidenceLevelId>({ placeholder: 'Select Level', initialValue: deal.confidenceLevel?.id }),
    [deal.confidenceLevel?.id],
  );
  const statusItems = useMemo(() => CommonService.selectItems(dealStatuses, 'name', 'id', false), [dealStatuses]);
  const confidenceLevelItems = useMemo(() => CommonService.selectItems(confidenceLevels, 'name', 'id', false), [confidenceLevels]);

  const onStatus = useCallback(
    (dealStatusId: DealStatusId) => {
      const selected = dealStatuses.find(s => s.id === dealStatusId);
      const statusEnum = selected?.status as DealStatus | null;

      // Built-in lost status, or ANY matching rule (Settings → Deals → Disqualification Rules),
      // asks for a reason — titled per the rule's outcome (Lost vs Disqualified).
      const matchRule = (outcome: 'lost' | 'disqualified') =>
        disqualificationRules.some(
          rule =>
            (rule.outcome ?? 'disqualified') === outcome &&
            (rule.statusIds ?? []).map(String).includes(String(dealStatusId)) &&
            (rule.boardId == null || String(rule.boardId) === String(deal.board.id)),
        );
      const matchesLostRule = matchRule('lost');
      const matchesDisqualifiedRule = matchRule('disqualified');
      if (statusEnum && (statusEnum === DealStatus.lost || matchesLostRule || matchesDisqualifiedRule)) {
        openDealLostModal({
          dealId: deal.id,
          status: statusEnum,
          variant: statusEnum === DealStatus.lost || matchesLostRule ? 'lost' : 'disqualified',
          onDone: () => bloc.onReload(),
          numberOfProposals: deal.proposals.length,
        });
      }
      bloc.updateDealAction(deal.id, dealStatusId, 'dealStatusConditionId').subscribe(updated => {
        // Status → Stage automation may have moved the deal server-side; reflect it on the board.
        if (updated && updated.stageId && String(updated.stageId) !== String(deal.stageId)) {
          resolve(Blocs.dealBoard).moveDealToStageInBoards(deal.id, updated.stageId);
        }
      });
    },
    [bloc, deal, dealStatuses, disqualificationRules],
  );

  const customFields = useMemo(() => {
    if (!settings) return [];
    return R.pipe(
      settings.customFields,
      R.ops.filter(f => f.for === 'deals'),
      R.ops.map(field => ({
        id: field.id,
        title: field.label,
        value: getFieldValueString(field, deal.customFields[field.id] || ''),
        field,
      })),
      R.ops.filter(item => !R.isEmpty(item.value)),
    );
  }, [deal.customFields, settings]);

  const onAssignEstimator = useCallback(() => {
    openGenericSelectModal({
      title: 'Add Estimator to Deal',
      items: CommonService.selectItems(estimators, getEstimatorLabel, 'id', true),
      initialValue: deal.estimatorUserId,
      label: 'Select Estimator',
      onSubmit: estimatorId => bloc.updateDealAction(deal.id, estimatorId ?? '', 'estimatorUserId'),
    });
  }, [bloc, deal]);

  const onAssignContact = useCallback(() => {
    openSelectContactModal({
      onSelect: contactId => bloc.updateDealAction(deal.id, contactId, 'contact_id'),
      title: 'Select Contact',
      button: 'Update',
      orgId: deal.organization.id,
      contactId: deal.contact ? deal.contact.id : undefined,
      email: false,
      phone: false,
    });
  }, [bloc, deal]);

  const customFieldContent = useMemo(() => {
    return R.chunk(customFields, 5).map((fields, fIndex) => (
      <div key={fIndex} className="d-flex flex-wrap mb-4 project-line">
        {fields.map(field => (
          <TextBlock key={field.id} title={field.title}>
            {field.value}
          </TextBlock>
        ))}
        {fields.length < 5 && R.times(5 - fields.length, R.constant(<div style={{ flex: 1 }}></div>))}
      </div>
    ));
  }, [customFields]);

  const executiveSummary = useMemo(() => {
    if (IntegrationService.isServiceEnabled(IntegrationType.vendasta)) {
      const hasReport = !!deal.organization.extra.accountGroupId;
      return (
        <TextBlock
          title={
            <Fragment>
              Snapshot Report
              <a
                className="text-primary"
                onClick={() =>
                  openSelectVendastaAccountModal({
                    type: 'deal',
                    objectId: deal.id,
                    boardId: deal.organization.id,
                    value:
                      deal.organization.vendastaName && deal.organization.vendastaId
                        ? { companyName: deal.organization.vendastaName, accountGroupId: deal.organization.vendastaId }
                        : undefined,
                  })
                }
              >
                <i className={`bx bx-edit ml-2 text-primary`}></i>
              </a>
            </Fragment>
          }
        >
          {hasReport && (
            <a
              className=""
              href={hasReport ? R.get(deal, 'organization.extra.reportUrl') ?? undefined : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
              <i className={`fas fa-external-link-alt ml-2 text-primary`}></i>
            </a>
          )}
          {!hasReport && (
            <a
              onClick={() =>
                openSelectVendastaAccountModal({
                  type: 'deal',
                  objectId: deal.id,
                  boardId: deal.organization.id,
                  value:
                    deal.organization.vendastaName && deal.organization.vendastaId
                      ? { companyName: deal.organization.vendastaName, accountGroupId: deal.organization.vendastaId }
                      : undefined,
                })
              }
              className="text-primary"
            >
              Select
            </a>
          )}
        </TextBlock>
      );
    }
    return <div />;
  }, [deal]);

  const intervalValue = useMemo(() => {
    if (deal.interval === 'one time') {
      return '';
    } else {
      return ' / ' + deal.interval;
    }
  }, [deal]);

  return (
    <Card>
      <CardBody>
        <div className="detail-overlay">
          <div className="d-flex flex-wrap mb-4 project-line">
            <TextBlock title={gcLabel('Organization')}>
              <a className="text-primary" onClick={() => openOrganizationOverlay({ orgId: deal.orgId })}>
                {deal.organization.name}
              </a>
            </TextBlock>
            <TextBlock
              title={
                <>
                  {gcLabel('Contact')}
                  <a className="ml-2">
                    <i className="bx bx-edit font-size-16" onClick={onAssignContact}></i>
                  </a>
                </>
              }
            >
              {deal.contact ? (
                <>
                  <a href={`/people/${deal.contact.id}`}>{CommonService.getFullName(deal.contact)}</a>
                  {!!deal.contact.email && (
                    <Fragment>
                      <br />
                      <ContactEmailText org={deal.organization} email={deal.contact.email} />
                    </Fragment>
                  )}
                  {!!deal.contact.phone && (
                    <div className="d-flex align-items-center">
                      {deal.contact.phone} {deal.contact.phoneExt ? 'Ext: ' + deal.contact.phoneExt : ''}
                      <PhoneActions phone={deal.contact.phone} />
                    </div>
                  )}
                </>
              ) : (
                <a className="text-primary" onClick={onAssignContact}>
                  Select Contact
                </a>
              )}
            </TextBlock>
            {!deal.noValue && (
              <>
                <HasBilling>
                  <TextBlock title="Value">
                    {Formatter.formatCurrency(deal.valueDefault, company.currencyCode)}
                    {intervalValue}
                  </TextBlock>
                </HasBilling>
              </>
            )}

            <TextBlock title="Expected Closed Date">
              <div className="d-flex flex-row">
                <span>{Formatter.formatTime(deal.expectedClosed)}</span>
                <a
                  className="d-flex ml-2"
                  onClick={() =>
                    openSelectDateModal({
                      title: 'Update Expected Closed Date',
                      initialValue: deal.expectedClosed,
                      minDate: toMoment().unix(),
                      onSubmit: date => bloc.updateClosedDate(deal.id, date),
                    })
                  }
                >
                  <i className="bx bx-calendar font-size-16 text-primary"></i>
                </a>
              </div>
            </TextBlock>
            <TextBlock title="Estimator" style={{ flex: 1, marginRight: 16 }}>
              {selectedEstimator ? (
                <Fragment>
                  <span>{CommonService.getFullName(selectedEstimator)}</span>
                  <a className="ml-2" onClick={onAssignEstimator}>
                    <i className="bx bx-edit font-size-16"></i>
                  </a>
                </Fragment>
              ) : (
                <a className="text-primary" onClick={onAssignEstimator}>
                  Assign Estimator
                </a>
              )}
              {selectedEstimator && <EstimatorAvailabilityBadge estimator={selectedEstimator} className="mt-2" />}
            </TextBlock>
            <TextBlock title="Status" style={{ flex: 1, marginRight: 16 }}>
              <FormSelect control={statusControl} items={statusItems} withoutGroup onChange={onStatus} />
            </TextBlock>
            <TextBlock title="Confidence Level" style={{ flex: 1 }}>
              <FormSelect
                control={confidenceLevelControl}
                items={confidenceLevelItems}
                withoutGroup
                onChange={id => bloc.updateDealAction(deal.id, id ?? '', 'confidenceLevelId').subscribe()}
              />
            </TextBlock>
            {executiveSummary}
          </div>
          {customFieldContent}
        </div>
      </CardBody>
    </Card>
  );
});
